import React, { useState, useEffect } from 'react';
import {
  X,
  Smartphone,
  Monitor,
  ShieldCheck,
  CheckCircle2,
  Copy,
  Zap,
  Lock,
  ArrowRight,
  HelpCircle,
  Bell,
  Sliders,
  Sparkles,
  ExternalLink,
  ChevronRight
} from 'lucide-react';
import { CkLogo } from './CkLogo';

interface SetupGuideModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialTab?: 'android' | 'windows' | 'security';
  onOpenDownloads?: () => void;
}

export const SetupGuideModal: React.FC<SetupGuideModalProps> = ({
  isOpen,
  onClose,
  initialTab = 'android',
  onOpenDownloads
}) => {
  const [activeTab, setActiveTab] = useState<'android' | 'windows' | 'security'>(initialTab);

  useEffect(() => {
    if (isOpen && initialTab) {
      setActiveTab(initialTab);
    }
  }, [isOpen, initialTab]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-md animate-fadeIn">
      <div className="relative w-full max-w-4xl max-h-[90vh] flex flex-col bg-[#0f111a]/95 border border-cyan-500/30 rounded-2xl shadow-[0_0_50px_rgba(0,240,255,0.15)] overflow-hidden">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/10 bg-white/[0.02]">
          <div className="flex items-center gap-3">
            <CkLogo size={36} />
            <div>
              <h2 className="text-xl font-bold text-white tracking-wide">Step-by-Step Setup &amp; User Guide</h2>
              <p className="text-xs text-gray-400">Complete walkthrough for seamless Phone ⇄ PC clipboard synchronization</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Tab Selection */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-white/5 bg-black/20 overflow-x-auto">
          {[
            { id: 'android', label: '📱 Android Phone Setup', icon: Smartphone },
            { id: 'windows', label: '💻 Windows PC Companion', icon: Monitor },
            { id: 'security', label: '🔒 E2E Security & Whitepaper', icon: ShieldCheck },
          ].map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex items-center gap-2 px-4 py-2 text-xs font-medium rounded-xl transition-all whitespace-nowrap ${
                  isActive
                    ? 'bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 shadow-[0_0_15px_rgba(0,240,255,0.15)]'
                    : 'text-gray-400 hover:text-gray-200 hover:bg-white/5 border border-transparent'
                }`}
              >
                <Icon size={14} />
                {tab.label}
              </button>
            );
          })}
        </div>

        {/* Modal Body */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6 custom-scrollbar text-gray-300">
          
          {/* ANDROID SETUP TAB */}
          {activeTab === 'android' && (
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-gradient-to-r from-emerald-950/40 to-cyan-950/30 border border-emerald-500/20 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <Smartphone size={16} className="text-emerald-400" />
                    Android Phone Setup (5 Easy Steps)
                  </h3>
                  <p className="text-xs text-gray-400">Achieve 100% zero-click clipboard sync from any app on your phone to PC.</p>
                </div>
                {onOpenDownloads && (
                  <button
                    onClick={onOpenDownloads}
                    className="px-3 py-1.5 text-xs font-semibold text-emerald-300 bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-500/30 rounded-lg transition-all"
                  >
                    Download APK
                  </button>
                )}
              </div>

              {/* Step 1 */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 flex items-center justify-center text-xs font-bold">1</span>
                  <h4 className="text-sm font-semibold text-white">Install the Android APK</h4>
                </div>
                <p className="text-xs text-gray-400 pl-9 leading-relaxed">
                  Download <strong className="text-gray-200">ck-share-android.apk</strong> and open it. If prompted with <em className="text-gray-300">"Install unknown apps"</em>, toggle <strong>Allow from this source</strong> for your browser or file manager and complete installation.
                </p>
              </div>

              {/* Step 2 */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 flex items-center justify-center text-xs font-bold">2</span>
                  <h4 className="text-sm font-semibold text-white">Join Your Room</h4>
                </div>
                <p className="text-xs text-gray-400 pl-9 leading-relaxed">
                  Open the <strong>CK Share</strong> app on your phone. Enter your 4-digit Room Code (e.g. <strong className="text-cyan-300">CK-9872</strong>) and Password, then tap <strong>Enter Room</strong> (or scan the QR code from your PC).
                </p>
              </div>

              {/* Step 3 */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 flex items-center justify-center text-xs font-bold">3</span>
                  <h4 className="text-sm font-semibold text-white">Enable Accessibility Service (For Background Copy)</h4>
                </div>
                <p className="text-xs text-gray-400 pl-9 leading-relaxed">
                  Android 10+ restricts apps from reading clipboard in the background. Enabling Accessibility allows CK Share to intercept copied text across WhatsApp, Chrome, Notes, and other apps.
                </p>
                <div className="pl-9 pt-1">
                  <div className="p-3 rounded-lg bg-black/40 border border-white/5 text-xs text-gray-300 space-y-1">
                    <p className="font-semibold text-cyan-300">How to turn on:</p>
                    <p>Go to phone <strong>Settings ➔ Accessibility ➔ Installed Apps / Services ➔ CK Share Universal Sync ➔ Turn ON</strong>.</p>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 flex items-center justify-center text-xs font-bold">4</span>
                  <h4 className="text-sm font-semibold text-white">Persistent Notification &amp; 1-Tap Quick Sync</h4>
                </div>
                <p className="text-xs text-gray-400 pl-9 leading-relaxed">
                  You will see an ongoing notification: <span className="text-cyan-300 font-mono">Sync Active • Room: CK-XXXX</span>. This keeps the daemon alive even when you close the app.
                </p>
                <div className="pl-9 pt-1 flex items-center gap-2">
                  <div className="px-3 py-1.5 rounded-lg bg-cyan-500/10 border border-cyan-500/30 text-xs text-cyan-300 flex items-center gap-1.5">
                    <Zap size={13} />
                    <span><strong>⚡ Sync Clipboard Now</strong> button: Pull down notification shade anytime to force-sync current clipboard in 1 tap!</span>
                  </div>
                </div>
              </div>

              {/* Step 5 */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-cyan-500/20 text-cyan-300 border border-cyan-500/40 flex items-center justify-center text-xs font-bold">5</span>
                  <h4 className="text-sm font-semibold text-white">Test Copying!</h4>
                </div>
                <p className="text-xs text-gray-400 pl-9 leading-relaxed">
                  Go to <strong>Chrome, WhatsApp, or Notes</strong>, select any text, and tap <strong>Copy</strong>. Look at your PC screen ➔ The notification appears instantly, and pressing <strong className="text-white">Ctrl + V</strong> on PC pastes your text!
                </p>
              </div>

            </div>
          )}

          {/* WINDOWS SETUP TAB */}
          {activeTab === 'windows' && (
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-gradient-to-r from-purple-950/40 to-indigo-950/30 border border-purple-500/20 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <Monitor size={16} className="text-purple-400" />
                    Windows Desktop Companion Setup (3 Steps)
                  </h3>
                  <p className="text-xs text-gray-400">Zero-click clipboard synchronization directly from Windows System Tray.</p>
                </div>
                {onOpenDownloads && (
                  <button
                    onClick={onOpenDownloads}
                    className="px-3 py-1.5 text-xs font-semibold text-purple-300 bg-purple-500/10 hover:bg-purple-500/20 border border-purple-500/30 rounded-lg transition-all"
                  >
                    Download EXE
                  </button>
                )}
              </div>

              {/* Step 1 */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40 flex items-center justify-center text-xs font-bold">1</span>
                  <h4 className="text-sm font-semibold text-white">Download &amp; Run CKShareTray.exe</h4>
                </div>
                <p className="text-xs text-gray-400 pl-9 leading-relaxed">
                  Download <strong className="text-gray-200">CKShareTray.exe</strong> (or extract <strong className="text-gray-200">ck-share-windows.zip</strong>). Double-click to launch. It requires zero installation and uses 0% CPU at idle (~37 KB standalone).
                </p>
              </div>

              {/* Step 2 */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40 flex items-center justify-center text-xs font-bold">2</span>
                  <h4 className="text-sm font-semibold text-white">Enter Room Credentials</h4>
                </div>
                <p className="text-xs text-gray-400 pl-9 leading-relaxed">
                  Locate the glowing <strong>CK Share icon</strong> in your Windows System Tray (bottom-right near the clock, or in the hidden tray arrow <kbd>^</kbd>).
                </p>
                <div className="pl-9 pt-1">
                  <div className="p-3 rounded-lg bg-black/40 border border-white/5 text-xs text-gray-300 space-y-1">
                    <p>Right-click the icon ➔ Choose <strong>Settings</strong> ➔ Type in your <strong>Room ID</strong> and <strong>Access Code</strong> ➔ Click <strong>Save &amp; Connect</strong>.</p>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40 flex items-center justify-center text-xs font-bold">3</span>
                  <h4 className="text-sm font-semibold text-white">Zero-Click Copy &amp; Paste in Action</h4>
                </div>
                <div className="pl-9 space-y-2 text-xs text-gray-400 leading-relaxed">
                  <div className="flex items-start gap-2">
                    <ChevronRight size={14} className="text-purple-400 flex-shrink-0 mt-0.5" />
                    <span><strong>PC ➔ Phone:</strong> Select text in VS Code, Notepad, Chrome, or Word and press <strong className="text-white">Ctrl + C</strong>. It silently syncs to your phone instantly (no annoying self-notifications).</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <ChevronRight size={14} className="text-purple-400 flex-shrink-0 mt-0.5" />
                    <span><strong>Phone ➔ PC:</strong> Copy any text on your phone. A single toast notification appears in Windows, and the clipboard is ready to paste with <strong className="text-white">Ctrl + V</strong>!</span>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                <div className="flex items-center gap-3">
                  <span className="w-6 h-6 rounded-full bg-purple-500/20 text-purple-300 border border-purple-500/40 flex items-center justify-center text-xs font-bold">4</span>
                  <h4 className="text-sm font-semibold text-white">Native Drag &amp; Drop QuickDrop (No Web Needed!)</h4>
                </div>
                <p className="text-xs text-gray-400 pl-9 leading-relaxed">
                  Double-click the tray icon to open the <strong>QuickDrop Hub</strong>. Drag and drop any files (images, PDFs, documents, zips) to send directly to your phone. Files sent from your phone are automatically saved to <code className="text-cyan-300">Downloads\CK_Share</code>!
                </p>
              </div>

              {/* Auto-Start with Windows Tip */}
              <div className="p-4 rounded-xl bg-gradient-to-r from-purple-950/20 to-black/30 border border-purple-500/20 flex items-center gap-3">
                <Sliders size={18} className="text-purple-400 flex-shrink-0" />
                <p className="text-xs text-gray-300">
                  <strong className="text-white">Pro-tip:</strong> In the Tray Settings, check <strong>"Start with Windows"</strong> so CK Share runs quietly in the background whenever your PC boots up.
                </p>
              </div>

            </div>
          )}

          {/* SECURITY & WHITEPAPER TAB */}
          {activeTab === 'security' && (
            <div className="space-y-6">
              <div className="p-4 rounded-xl bg-gradient-to-r from-cyan-950/40 to-blue-950/30 border border-cyan-500/20 flex items-center justify-between flex-wrap gap-3">
                <div>
                  <h3 className="text-sm font-bold text-white flex items-center gap-2">
                    <ShieldCheck size={16} className="text-cyan-400" />
                    End-to-End Encryption &amp; Security Architecture
                  </h3>
                  <p className="text-xs text-gray-400">Zero-Knowledge cryptographic specifications for CK Share.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                  <div className="flex items-center gap-2 text-cyan-400 text-sm font-semibold">
                    <Lock size={15} />
                    <span>Client-Side AES-GCM-256</span>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    All clipboard snippets, quick drops, and metadata are encrypted on your local device before transmission using <strong>AES-GCM (Galois/Counter Mode) with 256-bit keys</strong> and random 12-byte initialization vectors (IVs).
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                  <div className="flex items-center gap-2 text-purple-400 text-sm font-semibold">
                    <Zap size={15} />
                    <span>PBKDF2 Key Derivation</span>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Encryption keys are derived from your room password using <strong>PBKDF2 with HMAC-SHA256 and 100,000 rounds</strong> + a unique cryptographic salt per payload. Passwords are never sent across the wire.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                  <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold">
                    <ShieldCheck size={15} />
                    <span>Zero-Knowledge Cloudflare Relay</span>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    The Cloudflare Workers &amp; Durable Objects relay acts solely as a stateless blind routing hub. It only sees opaque ciphertext. It cannot read, log, decrypt, or inspect your clipboard data.
                  </p>
                </div>

                <div className="p-4 rounded-xl bg-white/[0.02] border border-white/10 space-y-2">
                  <div className="flex items-center gap-2 text-blue-400 text-sm font-semibold">
                    <Sparkles size={15} />
                    <span>Zero Persistent Server Storage</span>
                  </div>
                  <p className="text-xs text-gray-400 leading-relaxed">
                    Messages are broadcast in-memory to active room peers and discarded. No database, logs, analytics, or user trackers are stored on any server.
                  </p>
                </div>

              </div>

              {/* Android Accessibility API Declaration */}
              <div className="p-4 rounded-xl bg-black/40 border border-white/10 space-y-2">
                <h4 className="text-xs font-bold text-white uppercase tracking-wider text-cyan-300">
                  Android Accessibility API Usage Declaration
                </h4>
                <p className="text-xs text-gray-400 leading-relaxed">
                  In compliance with Google Play Policy and Android OS guidelines: CK Share's Android Accessibility Service is utilized <strong>exclusively</strong> to detect clipboard copy actions across apps in real-time. It does <strong>not</strong> collect personal data, monitor keystrokes, access screen content for third parties, or read sensitive input fields. All processed text is handled strictly in local memory and encrypted immediately.
                </p>
              </div>

            </div>
          )}

        </div>

        {/* Modal Footer */}
        <div className="px-6 py-4 border-t border-white/10 bg-white/[0.02] flex items-center justify-between">
          <p className="text-xs text-gray-500">Need help? All guides are accessible anytime from the bottom navigation.</p>
          <button
            onClick={onClose}
            className="px-4 py-2 text-xs font-medium text-gray-300 hover:text-white bg-white/5 hover:bg-white/10 rounded-xl transition-colors"
          >
            Got it
          </button>
        </div>

      </div>
    </div>
  );
};
